/**
 * Posting rules — `/ledger/posting-rules`.
 *
 * This is the map from "something happened" to "these journal lines". For one
 * event type, each line says which side it posts, which payload field carries
 * its amount, and where its account comes from: a fixed account, or one looked
 * up per product category, tax class, or partner.
 *
 * The screen enforces the rule the ledger cares about most: a rule is editable
 * only until it is activated. After that the only permitted change is
 * deactivation, because the lines that priced past journals must stay exactly
 * as they were when they priced them. So an active rule shows its lines
 * read-only and offers Deactivate, never Edit.
 *
 * The dry run is the safe question — "what would this post?" — and it says so:
 * the answer is drawn as draft lines, labelled as committing nothing.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FlaskConical, Plus, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@erp/ui";

import { formatCalendarDate } from "@/lib/format";
import { localizedName } from "@/lib/i18n";
import { newIdempotencyKey } from "@/features/shared/ops-api";

import { useAccounts } from "../queries";
import {
  ACCOUNT_SOURCES,
  AMOUNT_FIELDS,
  EVENT_TYPES,
  createPostingRule,
  dryRunPostingRule,
  getPostingRule,
  listPostingRules,
  updatePostingRule,
  type AccountSource,
  type DryRunResult,
  type PostingRule,
  type PostingRuleLineInput,
  type PostingRuleSide,
} from "../posting-rules-api";

const ALL = "__all__";

type DraftLine = {
  role: string;
  side: PostingRuleSide;
  amount_field: string;
  account_source: AccountSource;
  fixed_account_id: string;
};

const emptyLine = (eventType: string): DraftLine => ({
  role: "",
  side: "debit",
  amount_field: AMOUNT_FIELDS[eventType] ?? "",
  account_source: "fixed",
  fixed_account_id: "",
});

export interface PostingRulesPageProps {
  canCreate?: boolean;
  canUpdate?: boolean;
  canPreview?: boolean;
  canReadAccounts?: boolean;
}

export function PostingRulesPage({
  canCreate = false,
  canUpdate = false,
  canPreview = false,
  canReadAccounts = true,
}: PostingRulesPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [eventType, setEventType] = useState<string>(ALL);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draftEvent, setDraftEvent] = useState<string>("goods_received");
  const [draftFrom, setDraftFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [draftLines, setDraftLines] = useState<DraftLine[]>([
    emptyLine("goods_received"),
    emptyLine("goods_received"),
  ]);
  const [probe, setProbe] = useState("");
  const [probeDate, setProbeDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [probeResult, setProbeResult] = useState<DryRunResult | null>(null);
  const [probeError, setProbeError] = useState<string | null>(null);

  const accountsQuery = useAccounts({ is_postable: true }, canReadAccounts);
  const accounts = useMemo(() => accountsQuery.data ?? [], [accountsQuery.data]);
  const accountLabel = (id: string | undefined): string => {
    if (id === undefined) return "—";
    const account = accounts.find((candidate) => candidate.id === id);
    return account === undefined
      ? id.slice(0, 8)
      : `${account.code} · ${localizedName(account.name, account.name_ar, i18n.language)}`;
  };

  const rules = useQuery({
    queryKey: ["ledger/posting-rules", eventType],
    queryFn: async ({ signal }) => [
      ...(await listPostingRules(eventType === ALL ? {} : { event_type: eventType }, signal)).items,
    ],
  });

  const rows = rules.data ?? [];
  const selected: PostingRule | undefined = rows.find((row) => row.id === selectedId) ?? rows[0];

  const day = (iso: string): string => {
    try {
      return formatCalendarDate(iso, i18n.language);
    } catch {
      return iso;
    }
  };

  const run = async (work: () => Promise<unknown>, message: string) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await queryClient.invalidateQueries({ queryKey: ["ledger/posting-rules"] });
    } catch {
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  const saveRule = () =>
    void run(
      async () => {
        const lines: PostingRuleLineInput[] = draftLines.map((line) => ({
          role: line.role.trim(),
          side: line.side,
          amount_field: line.amount_field.trim(),
          account_source: line.account_source,
          // A fixed account is required for `fixed` and forbidden for every
          // mapped source; sending one anyway is a 422 waiting to happen.
          ...(line.account_source === "fixed" ? { fixed_account_id: line.fixed_account_id } : {}),
        }));
        const created = await createPostingRule(
          { event_type: draftEvent, valid_from: draftFrom, active: false, lines },
          newIdempotencyKey(),
        );
        setCreating(false);
        setSelectedId(created.id);
      },
      t("ledger.postingRules.saveError", "The rule could not be saved."),
    );

  const setActive = (next: boolean) => {
    if (selected === undefined) return;
    void run(
      async () => {
        const { etag } = await getPostingRule(selected.id);
        if (etag === null) throw new Error("missing etag");
        await updatePostingRule(selected.id, { active: next }, etag);
      },
      t("ledger.postingRules.saveError", "The rule could not be saved."),
    );
  };

  const tryEvent = () => {
    setProbeError(null);
    setProbeResult(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(probe);
    } catch {
      setProbeError(t("ledger.postingRules.badJson", "That is not valid JSON."));
      return;
    }
    setBusy(true);
    void dryRunPostingRule({ event: parsed, posting_date: probeDate })
      .then((result) => setProbeResult(result))
      .catch(() =>
        setProbeError(
          t(
            "ledger.postingRules.dryRunError",
            "No rule resolved this event, or the payload is missing something it needs.",
          ),
        ),
      )
      .finally(() => setBusy(false));
  };

  const template = (type: string): string =>
    JSON.stringify(
      {
        aggregate_id: "00000000-0000-7000-8000-000000000000",
        aggregate_type: type === "goods_received" ? "goods_receipt_line" : "stock_movement",
        event_type: type,
        occurred_at: new Date().toISOString(),
        payload: {
          branch_id: "00000000-0000-7000-8000-000000000000",
          currency: "SAR",
          [AMOUNT_FIELDS[type] ?? "amount"]: "1000.0000",
          product_category_id: "00000000-0000-7000-8000-000000000000",
        },
      },
      null,
      2,
    );

  const ruleValid =
    draftLines.length >= 2 &&
    draftLines.every(
      (line) =>
        line.role.trim() !== "" &&
        line.amount_field.trim() !== "" &&
        (line.account_source !== "fixed" || line.fixed_account_id !== ""),
    );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold tracking-tight">
            {t("ledger.postingRules.title", "Posting rules")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "ledger.postingRules.subtitle",
              "How an event becomes journal lines: which side, which amount, and where each account comes from.",
            )}
          </p>
        </div>
        {canCreate ? (
          <Button
            type="button"
            onClick={() => {
              setDraftLines([emptyLine(draftEvent), emptyLine(draftEvent)]);
              setCreating(true);
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            {t("ledger.postingRules.new", "New rule")}
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="rule-event">{t("ledger.postingRules.eventType", "Event")}</Label>
          <Select value={eventType} onValueChange={setEventType}>
            <SelectTrigger id="rule-event" className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("filters.all", "All")}</SelectItem>
              {EVENT_TYPES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`ledger.eventType.${value}`, value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-[20rem_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("ledger.postingRules.listTitle", "Rules")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            {rules.isLoading ? (
              <p className="p-2 text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
            ) : rows.length === 0 ? (
              <p className="p-2 text-sm text-muted-foreground">
                {t("ledger.postingRules.empty", "No posting rule is defined yet.")}
              </p>
            ) : (
              <ul className="flex flex-col gap-1">
                {rows.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      aria-current={row.id === selected?.id}
                      onClick={() => setSelectedId(row.id)}
                      className="flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-start text-sm hover:bg-muted aria-[current=true]:bg-muted aria-[current=true]:font-medium"
                    >
                      <span className="min-w-0">
                        <span className="block truncate">
                          {t(`ledger.eventType.${row.event_type}`, row.event_type)}
                        </span>
                        <span className="block truncate text-xs opacity-70">
                          {t("ledger.postingRules.fromDate", "from {{date}}", {
                            date: day(row.valid_from),
                          })}
                        </span>
                      </span>
                      <Badge variant={row.active ? "success" : "outline"}>
                        {row.active
                          ? t("ledger.postingRules.active", "Active")
                          : t("ledger.postingRules.inactive", "Inactive")}
                      </Badge>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-col gap-4">
          {selected === undefined ? (
            <Card>
              <CardContent className="p-6 text-sm text-muted-foreground">
                {t("ledger.postingRules.pick", "Choose a rule to see the lines it posts.")}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>{t(`ledger.eventType.${selected.event_type}`, selected.event_type)}</h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                  <p className="text-muted-foreground">
                    {selected.activated_at === undefined
                      ? t("ledger.postingRules.neverActivated", "Never activated — still editable.")
                      : t(
                          "ledger.postingRules.activatedOn",
                          "Activated {{date}} — its lines are frozen from then on.",
                          { date: day(selected.activated_at.slice(0, 10)) },
                        )}
                  </p>
                  {canUpdate ? (
                    <Button
                      type="button"
                      size="sm"
                      variant={selected.active ? "outline" : "default"}
                      disabled={busy}
                      onClick={() => setActive(!selected.active)}
                    >
                      {selected.active
                        ? t("ledger.postingRules.deactivate", "Deactivate")
                        : t("ledger.postingRules.activate", "Activate")}
                    </Button>
                  ) : null}
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[44rem] text-sm">
                    <thead>
                      <tr className="border-b text-xs uppercase text-muted-foreground">
                        <th scope="col" className="p-2 text-start">
                          {t("ledger.postingRules.role", "Role")}
                        </th>
                        <th scope="col" className="p-2 text-start">
                          {t("ledger.postingRules.side", "Side")}
                        </th>
                        <th scope="col" className="p-2 text-start">
                          {t("ledger.postingRules.amountField", "Amount from")}
                        </th>
                        <th scope="col" className="p-2 text-start">
                          {t("ledger.postingRules.account", "Account")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.lines.map((line) => (
                        <tr key={line.id} className="border-b last:border-0">
                          <td className="p-2">{line.role}</td>
                          <td className="p-2">
                            <Badge variant="outline">
                              {t(`ledger.side.${line.side}`, line.side)}
                            </Badge>
                          </td>
                          <td className="p-2">
                            <span dir="ltr" className="font-mono text-xs">
                              {line.amount_field}
                            </span>
                          </td>
                          <td className="p-2">
                            {line.account_source === "fixed"
                              ? accountLabel(line.fixed_account_id)
                              : t(
                                  `ledger.accountSource.${line.account_source}`,
                                  line.account_source,
                                )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {canPreview ? (
            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>{t("ledger.postingRules.dryRunTitle", "Try an event")}</h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t(
                    "ledger.postingRules.dryRunHint",
                    "Resolve one event against the active rules and see the lines it would post. Nothing is committed.",
                  )}
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="probe-date">
                      {t("ledger.postingRules.postingDate", "Posting date")}
                    </Label>
                    <Input
                      id="probe-date"
                      type="date"
                      className="w-40"
                      value={probeDate}
                      onChange={(event) => setProbeDate(event.target.value)}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setProbe(template(selected?.event_type ?? "goods_received"))}
                  >
                    {t("ledger.postingRules.fillTemplate", "Insert a template")}
                  </Button>
                  <Button type="button" disabled={probe.trim() === "" || busy} onClick={tryEvent}>
                    <FlaskConical className="size-4" aria-hidden="true" />
                    {t("ledger.postingRules.dryRun", "Resolve")}
                  </Button>
                </div>
                <Textarea
                  aria-label={t("ledger.postingRules.eventJson", "Event JSON")}
                  dir="ltr"
                  rows={10}
                  className="font-mono text-xs"
                  value={probe}
                  onChange={(event) => setProbe(event.target.value)}
                />
                {probeError === null ? null : (
                  <p role="alert" className="text-sm text-destructive">
                    {probeError}
                  </p>
                )}
                {probeResult === null ? null : (
                  <div className="space-y-2">
                    <p className="text-sm">
                      {t(
                        "ledger.postingRules.dryRunResult",
                        "{{count}} line(s) — balanced, and not committed.",
                        { count: probeResult.lines.length },
                      )}
                    </p>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-xs uppercase text-muted-foreground">
                          <th scope="col" className="p-2 text-start">
                            {t("ledger.postingRules.role", "Role")}
                          </th>
                          <th scope="col" className="p-2 text-start">
                            {t("ledger.postingRules.account", "Account")}
                          </th>
                          <th scope="col" className="p-2 text-end">
                            {t("ledger.postingRules.amount", "Amount")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {probeResult.lines.map((line) => (
                          <tr key={line.line_no} className="border-b last:border-0">
                            <td className="p-2">{line.role}</td>
                            <td className="p-2">{accountLabel(line.account_id)}</td>
                            <td className="p-2 text-end tabular-nums" dir="ltr">
                              {line.amount} {line.currency}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>

      {creating ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("ledger.postingRules.new", "New rule")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t(
                "ledger.postingRules.formHint",
                "A rule is created inactive. Check it with a dry run, then activate it — after that its lines are frozen.",
              )}
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="new-rule-event">
                  {t("ledger.postingRules.eventType", "Event")}
                </Label>
                <Select
                  value={draftEvent}
                  onValueChange={(value) => {
                    setDraftEvent(value);
                    setDraftLines(
                      draftLines.map((line) => ({
                        ...line,
                        amount_field: AMOUNT_FIELDS[value] ?? line.amount_field,
                      })),
                    );
                  }}
                >
                  <SelectTrigger id="new-rule-event" className="w-56">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {t(`ledger.eventType.${value}`, value)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-rule-from">{t("ledger.postingRules.validFrom", "From")}</Label>
                <Input
                  id="new-rule-from"
                  type="date"
                  className="w-40"
                  value={draftFrom}
                  onChange={(event) => setDraftFrom(event.target.value)}
                />
              </div>
            </div>

            <ul className="flex flex-col gap-3">
              {draftLines.map((line, index) => (
                <li key={index} className="flex flex-wrap items-end gap-3 rounded-md border p-3">
                  <div className="space-y-1.5">
                    <Label htmlFor={`line-role-${index}`}>
                      {t("ledger.postingRules.role", "Role")}
                    </Label>
                    <Input
                      id={`line-role-${index}`}
                      dir="ltr"
                      className="w-40 font-mono"
                      placeholder="inventory"
                      value={line.role}
                      onChange={(event) =>
                        setDraftLines(
                          draftLines.map((row, rowIndex) =>
                            rowIndex === index ? { ...row, role: event.target.value } : row,
                          ),
                        )
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`line-side-${index}`}>
                      {t("ledger.postingRules.side", "Side")}
                    </Label>
                    <Select
                      value={line.side}
                      onValueChange={(value) =>
                        setDraftLines(
                          draftLines.map((row, rowIndex) =>
                            rowIndex === index ? { ...row, side: value as PostingRuleSide } : row,
                          ),
                        )
                      }
                    >
                      <SelectTrigger id={`line-side-${index}`} className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="debit">{t("ledger.side.debit", "Debit")}</SelectItem>
                        <SelectItem value="credit">{t("ledger.side.credit", "Credit")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`line-source-${index}`}>
                      {t("ledger.postingRules.accountSource", "Account from")}
                    </Label>
                    <Select
                      value={line.account_source}
                      onValueChange={(value) =>
                        setDraftLines(
                          draftLines.map((row, rowIndex) =>
                            rowIndex === index
                              ? {
                                  ...row,
                                  account_source: value as AccountSource,
                                  fixed_account_id: value === "fixed" ? row.fixed_account_id : "",
                                }
                              : row,
                          ),
                        )
                      }
                    >
                      <SelectTrigger id={`line-source-${index}`} className="w-56">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ACCOUNT_SOURCES.map((value) => (
                          <SelectItem key={value} value={value}>
                            {t(`ledger.accountSource.${value}`, value)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {line.account_source === "fixed" ? (
                    <div className="space-y-1.5">
                      <Label htmlFor={`line-account-${index}`}>
                        {t("ledger.postingRules.account", "Account")}
                      </Label>
                      <Select
                        value={line.fixed_account_id}
                        onValueChange={(value) =>
                          setDraftLines(
                            draftLines.map((row, rowIndex) =>
                              rowIndex === index ? { ...row, fixed_account_id: value } : row,
                            ),
                          )
                        }
                      >
                        <SelectTrigger id={`line-account-${index}`} className="w-64">
                          <SelectValue
                            placeholder={t("ledger.postingRules.pickAccount", "Pick…")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {accounts.map((account) => (
                            <SelectItem key={account.id} value={account.id}>
                              {account.code} ·{" "}
                              {localizedName(account.name, account.name_ar, i18n.language)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : null}
                  {draftLines.length > 2 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t("ledger.postingRules.removeLine", "Remove line")}
                      onClick={() =>
                        setDraftLines(draftLines.filter((_, rowIndex) => rowIndex !== index))
                      }
                    >
                      <Trash2 className="size-4" aria-hidden="true" />
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDraftLines([...draftLines, emptyLine(draftEvent)])}
              >
                {t("ledger.postingRules.addLine", "Add line")}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setCreating(false)}>
                {t("actions.cancel", "Cancel")}
              </Button>
              <Button type="button" disabled={!ruleValid || busy} onClick={saveRule}>
                {t("actions.save", "Save")}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
